<template>
  <v-row no-gutters>
    <v-col cols="12" lg="8" md="8">
      <p class="text-h2">
        {{ canEditOffer ? $t('STEPPER_STEP5_TITLE') : $t('STEPPER_STEP5_LABEL') }}
      </p>

      <p v-if="canEditOffer" class="text-body-2 grey-font">{{ $t('STEPPER_STEP5_DESCRIPTION') }}</p>

      <v-expansion-panels class="mb-6 mt-12 pa-1" v-model="panels">
        <v-expansion-panel
          v-for="(product, index) in selectedItems"
          :key="'product-' + index"
          class="mb-4"
          @click="individualZoneDispersion = manageHeaderClick(index)"
        >
          <v-expansion-panel-header
            disable-icon-rotate
            :expand-icon="!isExpanded(index) ? (canEditOffer ? 'mdi-pencil' : 'mdi-eye') : null"
            :class="isExpanded(index) ? 'mb-0 pb-0' : null"
          >
            <div class="d-flex justify-space-between">
              <p class="mb-1 text-body-2 dispersion__description">
                {{ getProductLabel(selectedItems[index]) }}
              </p>

              <v-fade-transition hide-on-leave>
                <p
                  v-if="isExpanded(index) && canEditOffer"
                  class="mr-12 text-body-2 grey-font pr-12"
                >
                  {{ $t('STEPPER_STEP5_EDITION_DESCRIPTION') }}
                </p>
              </v-fade-transition>

              <div v-if="!isExpanded(index)" class="d-flex dispersion__price">
                <span :class="`text-h4 ${!isProductDispersionSumsValid ? 'primary--text' : null} `">
                  {{ productDispersion[index] }} %
                </span>
              </div>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-text-field
              :label="$t('STEPPER_STEP5_FIELD_PRODUCT_DISPERSION')"
              type="number"
              class="dispersion__number mx-4 text-body-2"
              outlined
              hide-details
              v-model="currentProductDispersion"
              height="75px"
              suffix="%"
              @change="changeProductDispersionType()"
              :readonly="!canEditOffer"
              @focus="!canEditOffer && $event.target.blur()"
            />

            <p class="ma-4 pt-4 grey--text text-body-2">
              {{ $t('STEPPER_STEP5_FIELD_ZONES_DESCRIPTION') }}
            </p>

            <div class="d-flex" v-if="hasZoneDispersionErrors">
              <p class="ma-4 text-body-1 primary--text">
                {{ $t('STEPPER_STEP5_TOTAL_MESSAGE', { sum: zoneDispersionSums }) }}
                %
              </p>

              <v-chip class="ma-2" color="rgba(223, 0, 36, 0.12)">
                {{
                  $t('STEPPER_STEP5_FIELD_ZONES_CHIP_ERROR', {
                    sum: zoneDispersionSums,
                  })
                }}
                %
              </v-chip>
            </div>

            <v-row class="pl-3">
              <v-col
                cols="12"
                md="3"
                lg="2"
                class="px-0"
                v-for="(zone, zoneIndex) in zoneList[index]"
                :key="'zone-' + zoneIndex"
              >
                <v-text-field
                  :label="$t(zoneList[index][zoneIndex].desc_key)"
                  class="dispersion__number mx-4 text-body-2 prueba2"
                  outlined
                  hide-details
                  v-model="individualZoneDispersion[zoneIndex]"
                  height="75px"
                  suffix="%"
                  type="number"
                  @change="changeZoneDispersionType(zoneIndex)"
                  :readonly="!canEditOffer"
                  @focus="!canEditOffer && $event.target.blur()"
                />
              </v-col>
            </v-row>

            <div v-if="canEditOffer" class="d-flex justify-end">
              <v-btn
                text
                color="primary"
                class="link non-text-transformation display-btn"
                :ripple="false"
                @click="close(false)"
                >{{ $t('ADDITIONAL_SERV_EXPANSION_BUTTON_CANCEL') }}</v-btn
              >

              <v-btn
                text
                color="primary"
                :ripple="false"
                @click="close(true, index)"
                :disabled="hasZoneDispersionErrors"
                class="link non-text-transformation display-btn"
                >{{ $t('ADDITIONAL_SERV_EXPANSION_BUTTON_SAVE') }}</v-btn
              >
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>

    <SummaryCard>
      <template v-slot:save-and-reset-offer-buttons>
        <SaveAndResetOfferButtons @onSaveClicked="saveOffer" @onResetClicked="resetOffer" />
      </template>

      <template v-slot:offer-status-button>
        <UpdateOfferStatusButton />
      </template>
    </SummaryCard>
  </v-row>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { UPDATE_OFFER_DISPERSION } from '@/store/modules/offer/actions'
import { GET_OFFER_ITEM } from '@/store/modules/offer/getters'
import SummaryCard from '@/components/summary-card'
import SaveAndResetOfferButtons from '@/components/buttons/SaveAndResetOfferButtons'
import UpdateOfferStatusButton from '@/components/buttons/UpdateOfferStatusButton'

export default {
  name: 'Dispersion',
  components: {
    SummaryCard,
    SaveAndResetOfferButtons,
    UpdateOfferStatusButton,
  },
  data() {
    return {
      panels: [],
      individualZoneDispersion: [],
      currentProductDispersion: 0,
      zoneDispersionBackUp: [],
      currentProduct: '',
    }
  },
  computed: {
    ...mapGetters('offer', [GET_OFFER_ITEM, 'canEditOffer']),
    ...mapGetters('alerts', ['alertById']),
    getProductList() {
      return this[GET_OFFER_ITEM]('productList')
    },
    selectedItems() {
      return this.getProductList && this.getProductList.filter(this.isSelected).filter(this.isNotC14E)
    },
    productDispersion() {
      return (
        this.selectedItems &&
        this.selectedItems.map(product => product.dispersionRateByProduct * 100)
      )
    },
    zoneList() {
      return this.selectedItems && this.selectedItems.map(product => product.zoneList)
    },
    zonesDispersion() {
      return (
        this.selectedItems &&
        this.selectedItems.map(product =>
          product.zoneList.map(zone => zone.dispersionRateByZone * 100),
        )
      )
    },
    zoneDispersionSums() {
      return (
        this.individualZoneDispersion.length !== 0 &&
        this.individualZoneDispersion.reduce((a, b) => Number(a) + Number(b), 0)
      )
    },
    hasZoneDispersionErrors() {
      if (this.currentProductDispersion === 0) {
        return this.zoneDispersionSums !== 0
      } else {
        return this.zoneDispersionSums !== 100
      }
    },
    productDispersionSums() {
      return (
        this.productDispersion !== 0 &&
        this.productDispersion.reduce((a, b) => Number(a) + Number(b), 0)
      )
    },
    isProductDispersionSumsValid() {
      return this.productDispersionSums === 100
    },
    isRateModel2024() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2024"
    },
    isRateModel2025() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2025"
    },
    isRateModel2026() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2026"
    },
    isRateModel2026V2() {
      return this[GET_OFFER_ITEM]('rateModelDesc')?.contractModel === "PDF_VENTA_DIRECTA_2026_V2"
    },
    shouldCombineC14E() {
      return this.isRateModel2024 || this.isRateModel2025 || this.isRateModel2026 || this.isRateModel2026V2
    },
  },
  methods: {
    ...mapActions('offer', [UPDATE_OFFER_DISPERSION]),
    ...mapMutations('process', ['setIsDispersionFormValid']),
    ...mapActions('alerts', ['createAlert']),
    ...mapMutations('alerts', ['removeAlert']),
    setDispersion(dispersion) {
      this[UPDATE_OFFER_DISPERSION]({ dispersion })
    },
    saveOffer() {
      this.$emit('onSaveClicked')
    },
    resetOffer() {
      this.$emit('onResetClicked')
    },
    isSelected(element) {
      return element.isSelected || element.isPreSelected || element.isMandatory
    },
    isNotC14E(element) {
      return element.id !== "C14E"
    },
    getProductLabel(item) {
      // Para 24H en modelos que combinan C14E, mostrar naming especial
      if (this.shouldCombineC14E && item.id === '24H') {
        return this.$t('PRODUCT_24H') + ' / ' + this.$t('PRODUCT_C14E')
      }
      return this.$t(`PRODUCT_${item.desc_key}`)
    },
    isExpanded(index) {
      return this.panels === index
    },
    close(save, index) {
      if (save) {
        this.zonesDispersion[index] = [...this.individualZoneDispersion]
        const realIndex = this.getProductList.findIndex(product => product.id === this.selectedItems[this.currentProduct].id)
        let obj = {
          productIndex: realIndex,
          productDispersion: this.currentProductDispersion / 100,
          zoneDispersion: this.individualZoneDispersion.map(dispersion => dispersion / 100),
        }
        this.setDispersion(obj)
      }
      this.panels = []
    },
    manageHeaderClick(index) {
      if (!this.isExpanded(index)) {
        this.currentProduct = index
        this.currentProductDispersion = this.productDispersion[index]
        this.zoneDispersionBackUp = this.zonesDispersion.map(arr => arr.slice())[index]
      } else {
        this.currentProduct = ''
      }
      return this.zoneDispersionBackUp
    },
    changeZoneDispersionType(zoneIndex) {
      this.individualZoneDispersion[zoneIndex] = Number(this.individualZoneDispersion[zoneIndex])
    },
    changeProductDispersionType() {
      this.currentProductDispersion = Number(this.currentProductDispersion)
      if (this.currentProductDispersion < 0) {
        this.currentProductDispersion = 0
      } else if (this.currentProductDispersion > 100) {
        this.currentProductDispersion = 100
      }
    },
    formValidationChangeHandler() {
      this.$emit('onChangeIsContinueForbidden', !this.isProductDispersionSumsValid)
      this.setIsDispersionFormValid(this.isProductDispersionSumsValid)

      if (!this.isProductDispersionSumsValid) {
        if (!this.alertById('dispersion')) {
          this.createAlert({
            id: 'dispersion',
            type: 'error',
            contentKey: 'STEPPER_STEP5_ALERT_ERROR_BASIC',
            dismissible: false,
          })
        }
      } else {
        this.removeAlert('dispersion')
      }
    },
  },
  created() {
    this.formValidationChangeHandler()
  },
  watch: {
    isProductDispersionSumsValid() {
      this.formValidationChangeHandler()
    },
  },
}
</script>

<style scoped lang="scss">
.dispersion {
  &__description {
    white-space: nowrap;
  }
  &__price {
    width: 55%;
  }
  &__number {
    max-width: rem(120);
  }
}
</style>
